'use client';
import { FAQItem } from '@c/FAQSection';
import { Road2RecoveryIcon } from '@c/icons';
import Accordion from '@ui/Accordion';
import SafeImage from '@ui/SafeImage';
import Head from 'next/head';
import { useEffect, useState } from 'react';

const RoadToRecovery = () => {
  return (
    <>
      <Head>
        <title>MX Locker | Road 2 Recovery</title>
      </Head>
      <div className="flex min-h-[40rem] w-full  items-center justify-center bg-brand-darkest-white bg-gradient-to-r from-brand-light-green to-brand-dark-green">
        <div className="flex w-full max-w-[150rem] flex-col-reverse items-center justify-center gap-[6.3rem] px-[1.6rem] py-[7.05rem] md:flex-row md:justify-between md:gap-[0rem] md:px-[5rem] xl:px-[22.2rem] xl:py-[7.2rem]">
          <div className="hidden w-full items-center justify-center md:flex">
            <SafeImage
              src={'road2recovery.svg'}
              alt="header image"
              className="h-[15rem] w-[60rem]"
            />
          </div>
          <div className="flex w-full items-center justify-center md:hidden">
            <Road2RecoveryIcon />
          </div>
        </div>
      </div>

      <div className="max-w-[150rem] ">
        <div className="px-[1.6rem] py-[4rem] text-center text-[1.6rem] leading-[2.4rem] md:px-[5rem] xl:px-[22rem] xl:py-[10rem]">
          The Road 2 Recovery Foundation is a 501 (C) (3) non-profit
          organization founded to help AMA licensed professional
          motocross/supercross and action sport athletes with financial
          assistance if they sustain debilitating injury as well as providing
          motivational, emotional, and spiritual support to these individuals
          and their families.
        </div>

        <div className="w-full px-[1.6rem] py-[4rem] text-[1.6rem] md:px-[5rem] xl:px-[12rem] xl:py-[10rem]">
          <div className="mb-[4rem] text-center md:mb-[6.4rem]">
            <h1 className="mb-[1.6rem] text-[2.4rem] font-semibold leading-[3.2rem] md:text-[3.2rem] md:leading-[4.2rem]">
              Making an impact on the sport we all love.
            </h1>
            <p className="text-[1.6rem] leading-[2.4rem]">
              Road 2 Recovery is dedicated to making sure that every dollar
              donated to one of the athlete&apos;s funds goes as far as
              possible.
            </p>
          </div>

          <div className="flex w-full flex-col gap-[4rem] md:flex-row md:gap-0">
            <div className="flex w-full  flex-col gap-[1.6rem] text-center md:w-1/4">
              <div className="text-[1.8rem]  leading-[2.5rem]">SUPPORTING</div>
              <div className="text-[4.8rem] font-semibold  leading-[5.3rem] text-brand-mid-green">
                <AnimatedNumberCounter value={20} />
              </div>
              <div className="text-[1.6rem] font-semibold  leading-[2.4rem]">
                ATHLETES IN 2025
              </div>
            </div>

            <div className="flex w-full  flex-col gap-[1.6rem] text-center md:w-1/4">
              <div className="text-[1.8rem]  leading-[2.5rem]">RAISED OVER</div>
              <div className="text-[4.8rem] font-semibold  leading-[5.3rem] text-brand-mid-green">
                $<AnimatedNumberCounter value={305} />K
              </div>
              <div className="text-[1.6rem] font-semibold  leading-[2.4rem]">
                FOR ATHLETES IN 2025
              </div>
            </div>

            <div className="flex w-full  flex-col gap-[1.6rem] text-center md:w-1/4">
              <div className="text-[1.8rem]  leading-[2.5rem]">SUPPORTED</div>
              <div className="text-[4.8rem] font-semibold  leading-[5.3rem] text-brand-mid-green">
                <AnimatedNumberCounter value={100} />+
              </div>
              <div className="text-[1.6rem] font-semibold  leading-[2.4rem]">
                CAMPAIGNS
              </div>
            </div>

            <div className="flex w-full  flex-col gap-[1.6rem] text-center md:w-1/4">
              <div className="text-[1.8rem]  leading-[2.5rem]">RAISED OVER</div>
              <div className="text-[4.8rem] font-semibold  leading-[5.3rem] text-brand-mid-green">
                $<AnimatedNumberCounter value={9} />M
              </div>
              <div className="text-[1.6rem] font-semibold  leading-[2.4rem]">
                ALL TIME
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col px-[1.6rem] py-[4rem] leading-[2.4rem] md:flex-row md:px-[5rem] xl:px-[15rem] xl:py-[10rem]">
          <div className="mb-[4rem] flex w-full justify-center md:mb-[0rem] md:w-1/2 md:items-center md:justify-start">
            <div className="text-center text-[2.4rem] font-semibold leading-[4.2rem] md:text-left md:text-[3.2rem]">
              <span className="text-brand-mid-green">Bidding</span> Frequently
              <p>Asked Questions</p>
            </div>
          </div>

          <div className="w-full md:w-1/2">
            <FAQSection />
          </div>
        </div>
      </div>
    </>
  );
};

const FAQs: FAQItem[] = [
  {
    q: 'How does bidding work?',
    a: 'In an auction-style listing, the seller will set the starting price and you bid against other buyers. You can keep track of your bidding from the Bids/Offers section in your MX Locker dashboard.',
  },
  {
    q: 'What is a max bid?',
    a: 'To set up automatic bidding on a listing, enter the maximum amount you would like to pay for the item and select place bid. We will bid in increments on your behalf to keep you in the lead but only up to your limit. We will let you know if someone outbids you and you can decide if you want to increase your max bid.',
  },
  {
    q: 'Why was I immediately outbid?',
    a: 'If you are outbid immediately after placing a bid, it is likely that another bidder is using automatic bidding with a max bid higher than yours. You will need to increase your maximum bid in order to be the highest bidder.',
  },
  {
    q: 'Why do I need to enter payment information into my account to bid?',
    a: 'To ensure every auction is fair and functional, we verify that every bid placed is real and reliable. When you place your first bid on an auction, your entered payment information is charged if you are the highest bidder at the end of the auction. If the entered payment method fails, we will notify you immediately. If we do not receive a response, we will attempt to charge your other saved payment methods on file to finalize the transaction.',
  },
  {
    q: 'What happens if I have the same bid as someone else? ',
    a: `If you have the same bid as the highest bidder at the end of the auction, the person who bid earlier will win the auction. If you have the same max bid as someone else's during the auction, the earlier bidder will have their max bid increased by $1 to ensure the earliest bidder's success.`,
  },
  {
    q: 'I want to keep an eye on an auction. How can I receive updates? ',
    a: 'Just click the “favorite” (heart icon) on any auction you would like us to send updates on. Click it again to “unfavorite”. You can then view your favorite items from the favorites tab in your account.',
  },
  {
    q: 'Will I automatically get charged if I am the highest bidder at the end of the auction? ',
    a: 'Yes, bids are binding on MX Locker. Once a bid is made and the user is the highest bidder at the end of the auction, the sale transaction is binding on both Seller and Buyer. Once the auction is complete, the highest bid is completed instantly and the sale is made. As a buyer, your payment is only made if you are the highest bidder at the end of the auction. Until then, no money is taken from your account.',
  },
];

const FAQSection = () => {
  return (
    <div className="flex w-full flex-col gap-[1.6rem]">
      {FAQs.map((faq) => (
        <Accordion
          divider={true}
          highlightHeaderOnOpen={true}
          hover="text-green"
          intent="faq"
          key={faq.q}
          title={faq.q}
        >
          <p className="pl-[1.6rem]">{faq.a}</p>
        </Accordion>
      ))}
    </div>
  );
};

const AnimatedNumberCounter = ({
  value,
  duration = 10,
}: {
  value: number;
  duration?: number;
}) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (count < value) {
        setCount((count) => count + 1);
      }
    }, duration);

    return () => clearInterval(intervalId);
  }, [value, duration, count]);

  return <span>{count}</span>;
};

export default RoadToRecovery;
